import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useFamily } from '@/contexts/FamilyContext'
import { ChevronLeft, ChevronRight, Calendar, X, Eye } from 'lucide-react'
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addMonths, subMonths, startOfMonth, addYears, subYears, startOfYear } from 'date-fns'
import {
  usePointsHistory,
  useLeaderboard,
  usePointsBreakdown,
  PointTransaction,
  LeaderboardEntry,
  LeaderboardPeriod,
} from '@/hooks/usePoints'

export default function PointsOverview() {
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'leaderboard'>('overview')
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<LeaderboardPeriod>('week')
  const [selectedWeek, setSelectedWeek] = useState(new Date())
  const [selectedMonth, setSelectedMonth] = useState(new Date())
  const [selectedYear, setSelectedYear] = useState(new Date())
  const [showBreakdownModal, setShowBreakdownModal] = useState(false)
  const [selectedChildUsername, setSelectedChildUsername] = useState<string | null>(null)
  const [selectedKidId, setSelectedKidId] = useState<string | null>(null)
  const { user } = useAuth()
  const { getChildren } = useFamily()

  const children = getChildren()
  const isParent = user?.is_parent

  // Determine which user's data to display: selected kid (if parent) or logged-in user
  const displayUser = isParent && selectedKidId
    ? children.find(child => child.id === selectedKidId) || user
    : user

  // Default to first kid for parents
  useEffect(() => {
    if (isParent && children.length > 0 && selectedKidId === null) {
      setSelectedKidId(children[0].id)
    }
  }, [isParent, children, selectedKidId])

  // Calculate offset for period navigation
  const leaderboardOffset = useMemo(() => {
    const now = new Date()
    if (leaderboardPeriod === 'week') {
      const currentWeekStart = startOfWeek(now, { weekStartsOn: 0 })
      const selectedWeekStart = startOfWeek(selectedWeek, { weekStartsOn: 0 })
      const weekDiff = Math.floor((currentWeekStart.getTime() - selectedWeekStart.getTime()) / (1000 * 60 * 60 * 24 * 7))
      return -weekDiff
    } else if (leaderboardPeriod === 'month') {
      const currentMonthStart = startOfMonth(now)
      const selectedMonthStart = startOfMonth(selectedMonth)
      const yearDiff = currentMonthStart.getFullYear() - selectedMonthStart.getFullYear()
      const monthDiff = currentMonthStart.getMonth() - selectedMonthStart.getMonth()
      return -(yearDiff * 12 + monthDiff)
    } else if (leaderboardPeriod === 'year') {
      const currentYearStart = startOfYear(now)
      const selectedYearStart = startOfYear(selectedYear)
      return -(currentYearStart.getFullYear() - selectedYearStart.getFullYear())
    }
    return 0
  }, [leaderboardPeriod, selectedWeek, selectedMonth, selectedYear])

  // Determine target user ID for history
  const targetUserId = isParent && selectedKidId ? selectedKidId : user?.id

  // React Query hooks
  const { data: historyData, isLoading: historyLoading } = usePointsHistory(targetUserId, 10)
  const { data: leaderboard = [], isLoading: leaderboardLoading } = useLeaderboard(leaderboardPeriod, leaderboardOffset)
  const { data: childBreakdown } = usePointsBreakdown(
    selectedChildUsername,
    leaderboardPeriod,
    Math.abs(leaderboardOffset)
  )

  // Process points history with user info
  const pointsHistory = useMemo(() => {
    const transactions = historyData?.transactions || []
    const kidInfo = children.find(c => c.id === targetUserId)
    return transactions.map((t: PointTransaction) => ({
      ...t,
      user_name: kidInfo?.display_name || displayUser?.display_name,
      user_color: kidInfo?.color_theme || displayUser?.color_theme
    }))
  }, [historyData, children, targetUserId, displayUser])

  const isLoading = historyLoading || leaderboardLoading

  const fetchChildBreakdown = (username: string) => {
    setSelectedChildUsername(username)
    setShowBreakdownModal(true)
  }

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'chore_completion': return '✅'
      case 'weekly_bonus': return '🎯'
      case 'streak_bonus': return '🔥'
      case 'purchase': return '🛒'
      case 'trade': return '🔄'
      default: return '💰'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday' 
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString()
  }

  const getLevelProgress = () => {
    if (!displayUser) return 0
    const level = displayUser.level ?? 1
    const xp = displayUser.xp ?? 0
    // Simple level calculation: 100 XP per level
    const currentLevelXP = (level - 1) * 100
    const nextLevelXP = level * 100
    const progress = ((xp - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100
    return Math.max(0, Math.min(100, progress))
  }

  const getUserRank = () => {
    if (!displayUser) return 0
    const rank = leaderboard.findIndex(entry => entry.id === displayUser.id) + 1
    return rank || leaderboard.length + 1
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading stats...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Parent Kid Selector - Only show on Overview and History tabs, not Leaderboard */}
      {isParent && children.length > 0 && activeTab !== 'leaderboard' && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            View Points For:
          </label>
          <select
            value={selectedKidId || ''}
            onChange={(e) => setSelectedKidId(e.target.value || null)}
            className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Kids</option>
            {children.map(child => (
              <option key={child.id} value={child.id}>
                {child.display_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tab Navigation - Sticky on scroll */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg sticky top-0 z-20">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'overview'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📊 Overview
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'history'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📋 History
        </button>
        <button
          onClick={() => setActiveTab('leaderboard')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'leaderboard'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          🏆 Leaderboard
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && displayUser && (
        <div className="space-y-6">
          {/* Main Stats - Colorful Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Available Points</div>
              <div className="text-3xl font-bold">{(displayUser.available_points ?? displayUser.total_points ?? 0).toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">available to spend</div>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Lifetime Earned</div>
              <div className="text-3xl font-bold">{(displayUser.lifetime_points_earned ?? 0).toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">all-time earnings</div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">This Week</div>
              <div className="text-3xl font-bold">{(displayUser.weekly_points ?? 0).toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">earned this week</div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Level</div>
              <div className="text-3xl font-bold">{displayUser.level ?? 1}</div>
              <div className="text-xs opacity-75 mt-1">{displayUser.xp ?? 0} XP total</div>
            </div>

            <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Streak</div>
              <div className="text-3xl font-bold">{displayUser.streak_days ?? 0}</div>
              <div className="text-xs opacity-75 mt-1">days in a row</div>
            </div>
          </div>

          {/* Level Progress Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⭐</span>
                <h3 className="font-bold text-gray-900">Level {displayUser.level ?? 1}</h3>
              </div>
              <span className="text-sm font-medium text-gray-600">
                {displayUser.xp ?? 0} / {(displayUser.level ?? 1) * 100} XP
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className="h-4 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 transition-all duration-500"
                style={{ width: `${getLevelProgress()}%` }}
              ></div>
            </div>
            <div className="text-sm text-gray-500 mt-2 text-center">
              {Math.round(100 - getLevelProgress())}% to Level {(displayUser.level ?? 1) + 1}
            </div>
          </div>

          {/* Quick Stats Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-xl">📊</span> Quick Stats
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Rank</span>
                <span className="font-bold text-blue-600 text-lg">#{getUserRank()}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">This Week</span>
                <span className="font-bold text-green-600 text-lg">+{displayUser.weekly_points ?? 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Streak</span>
                <span className="font-bold text-orange-600 text-lg">{displayUser.streak_days ?? 0} days 🔥</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Level</span>
                <span className="font-bold text-purple-600 text-lg">Level {displayUser.level ?? 1}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Recent Points History</h2>
          <div className="text-sm text-gray-600 mb-3">
            📋 {isParent && !selectedKidId ? 'Last 20 Transactions (All Kids)' : 'Last 10 Transactions'}
          </div>

          {pointsHistory.length === 0 ? (
            <div className="card text-center py-8">
              <div className="text-4xl mb-2">📊</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No points history yet!
              </h3>
              <p className="text-gray-600">
                Complete some chores to start earning points and building your history.
              </p>
            </div>
          ) : (
            <div className="card">
              <div className="space-y-3">
                {pointsHistory.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">
                        {getTransactionIcon(transaction.transaction_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                          {transaction.description}
                          {/* Show kid name badge when viewing all kids or always for parents */}
                          {transaction.user_name && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                              style={{ backgroundColor: transaction.user_color || '#6B7280' }}
                            >
                              {transaction.user_name}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                          <span>{formatDate(transaction.created_at)}</span>
                          <span className="text-gray-300">•</span>
                          <span className="text-gray-400 text-xs">
                            {new Date(transaction.created_at).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`font-bold text-lg whitespace-nowrap ${
                      transaction.points > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.points > 0 ? '+' : ''}{transaction.points}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div>
          <h2 className="text-xl font-semibold mb-2 flex items-center">
            🏆 Family Leaderboard
            <span className="ml-2 text-sm font-normal text-gray-600">
              Who's earning the most points?
            </span>
          </h2>
          
          {/* Period Selector */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setLeaderboardPeriod('day')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                leaderboardPeriod === 'day'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setLeaderboardPeriod('week')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                leaderboardPeriod === 'week'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setLeaderboardPeriod('month')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                leaderboardPeriod === 'month'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setLeaderboardPeriod('year')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                leaderboardPeriod === 'year'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Year
            </button>
            <button
              onClick={() => setLeaderboardPeriod('all')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                leaderboardPeriod === 'all'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Time
            </button>
          </div>
          
          {/* Week Navigator (only for week view) */}
          {leaderboardPeriod === 'week' && (
            <div className="flex items-center justify-between mb-4 bg-gray-50 rounded-lg p-2">
              <button
                onClick={() => setSelectedWeek(subWeeks(selectedWeek, 1))}
                className="p-1 rounded hover:bg-gray-200 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {format(startOfWeek(selectedWeek, { weekStartsOn: 0 }), 'MMM d')} -
                {format(endOfWeek(selectedWeek, { weekStartsOn: 0 }), 'MMM d, yyyy')}
              </div>

              <button
                onClick={() => setSelectedWeek(addWeeks(selectedWeek, 1))}
                className="p-1 rounded hover:bg-gray-200 transition-colors"
                disabled={selectedWeek >= new Date()}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Month Navigator (only for month view) */}
          {leaderboardPeriod === 'month' && (
            <div className="flex items-center justify-between mb-4 bg-gray-50 rounded-lg p-2">
              <button
                onClick={() => setSelectedMonth(subMonths(selectedMonth, 1))}
                className="p-1 rounded hover:bg-gray-200 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {format(selectedMonth, 'MMMM yyyy')}
              </div>

              <button
                onClick={() => setSelectedMonth(addMonths(selectedMonth, 1))}
                className="p-1 rounded hover:bg-gray-200 transition-colors"
                disabled={startOfMonth(selectedMonth) >= startOfMonth(new Date())}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Year Navigator (only for year view) */}
          {leaderboardPeriod === 'year' && (
            <div className="flex items-center justify-between mb-4 bg-gray-50 rounded-lg p-2">
              <button
                onClick={() => setSelectedYear(subYears(selectedYear, 1))}
                className="p-1 rounded hover:bg-gray-200 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {format(selectedYear, 'yyyy')}
              </div>

              <button
                onClick={() => setSelectedYear(addYears(selectedYear, 1))}
                className="p-1 rounded hover:bg-gray-200 transition-colors"
                disabled={startOfYear(selectedYear) >= startOfYear(new Date())}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
          
          {leaderboard.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 text-center py-8">
              <div className="text-4xl mb-2">🏆</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No rankings yet!
              </h3>
              <p className="text-gray-600">
                Start earning points to see the leaderboard fill up.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((entry, index) => {
                const isTop3 = entry.rank <= 3
                const medalColors: Record<number, string> = {
                  1: 'from-yellow-400 to-amber-500',
                  2: 'from-gray-300 to-gray-400',
                  3: 'from-orange-400 to-amber-600'
                }
                const medalEmoji: Record<number, string> = {
                  1: '🥇',
                  2: '🥈',
                  3: '🥉'
                }

                return (
                  <div
                    key={entry.id}
                    className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:shadow-lg ${
                      isTop3
                        ? `bg-gradient-to-r ${medalColors[entry.rank]} text-white shadow-md`
                        : entry.id === user?.id
                          ? 'bg-blue-50 border-2 border-blue-300 shadow-sm'
                          : 'bg-white border border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => {
                      fetchChildBreakdown(entry.username)
                    }}
                  >
                    <div className="flex items-center space-x-4">
                      {isTop3 ? (
                        <div className="text-3xl">{medalEmoji[entry.rank]}</div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600">
                          #{entry.rank}
                        </div>
                      )}
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm ${
                          isTop3 ? 'ring-2 ring-white/50' : ''
                        }`}
                        style={{ backgroundColor: entry.color_theme }}
                      >
                        {entry.display_name[0]}
                      </div>
                      <div>
                        <div className={`font-semibold flex items-center gap-2 ${isTop3 ? 'text-white' : 'text-gray-900'}`}>
                          {entry.display_name}
                          {entry.id === user?.id && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${isTop3 ? 'bg-white/30' : 'bg-blue-100 text-blue-600'}`}>You</span>
                          )}
                          <Eye className={`w-4 h-4 ${isTop3 ? 'text-white/70' : 'text-gray-400'}`} />
                        </div>
                        <div className={`text-sm ${isTop3 ? 'text-white/80' : 'text-gray-500'}`}>
                          Level {entry.level} • {entry.streak_days} day streak
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${isTop3 ? 'text-white' : ''}`} style={isTop3 ? {} : { color: entry.color_theme }}>
                        {entry.points?.toLocaleString()}
                      </div>
                      <div className={`text-sm ${isTop3 ? 'text-white/80' : 'text-gray-500'}`}>
                        {leaderboardPeriod === 'day' ? 'today' :
                         leaderboardPeriod === 'week' ? 'this week' :
                         leaderboardPeriod === 'month' ? 'this month' :
                         leaderboardPeriod === 'year' ? 'this year' :
                         leaderboardPeriod === 'all' ? 'all time' :
                         'points'}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Points Breakdown Modal */}
      {showBreakdownModal && childBreakdown && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            {/* Receipt Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{childBreakdown.user}</h2>
                  <p className="text-sm opacity-90">Points Statement</p>
                  <p className="text-xs opacity-75 mt-1">{childBreakdown.period}</p>
                </div>
                <button
                  onClick={() => {
                    setShowBreakdownModal(false)
                    setSelectedChildUsername(null)
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Receipt Body */}
            <div className="p-6">
              {/* Chores Section */}
              {childBreakdown.breakdown.chores && childBreakdown.breakdown.chores.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">CHORES COMPLETED</h3>
                  <div className="space-y-1">
                    {childBreakdown.breakdown.chores.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description.replace('Completed: ', '')}</span>
                        </div>
                        <span className="text-green-600 font-mono">+{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-green-600 font-mono font-bold">+{childBreakdown.totals.earned}</span>
                  </div>
                </div>
              )}

              {/* Bonuses Section */}
              {childBreakdown.breakdown.bonuses && childBreakdown.breakdown.bonuses.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">BONUSES</h3>
                  <div className="space-y-1">
                    {childBreakdown.breakdown.bonuses.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description}</span>
                        </div>
                        <span className="text-blue-600 font-mono">+{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-blue-600 font-mono font-bold">+{childBreakdown.totals.bonuses}</span>
                  </div>
                </div>
              )}

              {/* Deductions Section */}
              {childBreakdown.breakdown.deductions && childBreakdown.breakdown.deductions.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">DEDUCTIONS</h3>
                  <div className="space-y-1">
                    {childBreakdown.breakdown.deductions.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description}</span>
                        </div>
                        <span className="text-red-600 font-mono">{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-red-600 font-mono font-bold">{childBreakdown.totals.deductions}</span>
                  </div>
                </div>
              )}

              {/* Final Calculations */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xs font-semibold text-gray-500 mb-4">CALCULATION</h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Earned from Chores</span>
                    <span className="font-mono">+{childBreakdown.totals.earned}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Bonuses</span>
                    <span className="font-mono">+{childBreakdown.totals.bonuses}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Deductions</span>
                    <span className="font-mono">{childBreakdown.totals.deductions}</span>
                  </div>
                  {childBreakdown.totals.adjustments !== 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Adjustments</span>
                      <span className="font-mono">{childBreakdown.totals.adjustments > 0 ? '+' : ''}{childBreakdown.totals.adjustments}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 mt-2 border-t text-gray-900 font-semibold">
                    <span>LEADERBOARD TOTAL</span>
                    <span className="font-mono text-blue-600 text-lg">{childBreakdown.totals.leaderboard_total}</span>
                  </div>
                </div>

                {/* Available Balance if they have spent points */}
                {childBreakdown.totals.spent > 0 && (
                  <div className="mt-4 pt-4 border-t space-y-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Rewards Redeemed</span>
                      <span className="font-mono">-{childBreakdown.totals.spent}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 mt-2 border-t text-gray-900 font-semibold">
                      <span>AVAILABLE BALANCE</span>
                      <span className="font-mono text-green-600 text-lg">{childBreakdown.totals.available_balance}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}